import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, User, Store, Search, Menu, LogOut, Heart, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/src/lib/AuthContext";
import { useCart } from "@/src/lib/CartContext";
import { useWishlist } from "@/src/lib/WishlistContext";
import { auth } from "@/src/lib/firebase";
import { toast } from "sonner";
import { useState } from "react";

export function Navbar() {
  const { user, profile, loading, hasClubCard } = useAuth();
  const { cart } = useCart();
  const { wishlist } = useWishlist();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await auth.signOut();
      toast.success("Sesión cerrada correctamente");
      navigate("/");
    } catch (error) {
      toast.error("Error al cerrar sesión");
    }
  };

  const [isOpen, setIsOpen] = useState(false);

  const cartCount = cart.reduce((acc, item) => acc + item.quantity, 0);
  const wishlistCount = wishlist.length;

  return (
    <nav className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between px-4 md:px-8">
        <div className="flex items-center gap-6 md:gap-10">
          <Link to="/" className="flex items-center space-x-2">
            <Store className="h-6 w-6 text-primary" />
            <span className="inline-block font-bold text-xl tracking-tight">
              Empre<span className="text-primary">Market</span>
            </span>
          </Link>
          <div className="hidden md:flex gap-6">
            <Link to="/shops" className="text-sm font-medium transition-colors hover:text-primary">
              Tiendas
            </Link>
            <Link to="/products" className="text-sm font-medium transition-colors hover:text-primary">
              Productos
            </Link>
            <Link to="/services" className="text-sm font-medium transition-colors hover:text-primary">
              Servicios
            </Link>
            <Link to="/pricing" className="text-sm font-medium transition-colors hover:text-primary">
              Precios
            </Link>
            <Link to="/blog" className="text-sm font-medium transition-colors hover:text-primary whitespace-nowrap">
              Blog
            </Link>
            <Link to="/club" className="text-sm font-medium transition-colors hover:text-primary whitespace-nowrap">
              Mall Virtual
            </Link>
            <Link to="/announcements" className="text-sm font-medium transition-colors hover:text-primary whitespace-nowrap">
              Avisos
            </Link>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden md:flex relative w-full max-w-sm items-center">
            <Search className="absolute left-2.5 h-4 w-4 text-muted-foreground" />
            <input
              type="search"
              placeholder="Buscar tiendas o productos..."
              className="h-9 w-64 rounded-md border border-input bg-background px-9 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          <div className="flex items-center gap-1">
            <Link to="/wishlist" className="hidden sm:block">
              <Button variant="ghost" size="icon" className="relative">
                <Heart className="h-5 w-5" />
                {wishlistCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                    {wishlistCount}
                  </span>
                )}
              </Button>
            </Link>

            <Link to="/cart">
              <Button variant="ghost" size="icon" className="relative">
                <ShoppingCart className="h-5 w-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                    {cartCount}
                  </span>
                )}
              </Button>
            </Link>
          </div>

          {!loading && (
            user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={profile?.photoURL || ""} alt={profile?.displayName} />
                      <AvatarFallback>{profile?.displayName?.charAt(0) || "U"}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end">
                  <DropdownMenuGroup>
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium leading-none">{profile?.displayName}</p>
                          {hasClubCard && (
                            <Badge className="h-4 px-1.5 text-[8px] bg-gradient-to-r from-amber-400 to-amber-600 border-none text-white whitespace-nowrap">
                              CLUB
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs leading-none text-muted-foreground">{profile?.email}</p>
                      </div>
                    </DropdownMenuLabel>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile">Mi Perfil</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/wishlist">Mis Favoritos</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/orders">Mis Pedidos</Link>
                  </DropdownMenuItem>
                  {profile?.role === "seller" ? (
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard">Panel de Vendedor</Link>
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem asChild>
                      <Link to="/become-seller">Quiero ser Vendedor</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Cerrar Sesión</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm">
                    Ingresar
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm">Registrarse</Button>
                </Link>
              </div>
            )
          )}

          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right">
              <div className="flex flex-col gap-4 mt-8">
                <Link to="/shops" className="text-lg font-semibold" onClick={() => setIsOpen(false)}>
                  Tiendas
                </Link>
                <Link to="/products" className="text-lg font-semibold" onClick={() => setIsOpen(false)}>
                  Productos
                </Link>
                <Link to="/services" className="text-lg font-semibold" onClick={() => setIsOpen(false)}>
                  Servicios
                </Link>
                <Link to="/pricing" className="text-lg font-semibold" onClick={() => setIsOpen(false)}>
                  Precios
                </Link>
                <Link to="/blog" className="text-lg font-semibold" onClick={() => setIsOpen(false)}>
                  Blog
                </Link>
                <Link to="/club" className="text-lg font-semibold" onClick={() => setIsOpen(false)}>
                  Mall Virtual
                </Link>
                <Link to="/announcements" className="text-lg font-semibold" onClick={() => setIsOpen(false)}>
                  Avisos
                </Link>
                <Separator />
                <Link to="/wishlist" className="text-lg font-semibold" onClick={() => setIsOpen(false)}>
                  Mis Favoritos
                </Link>
                <Link to="/cart" className="text-lg font-semibold" onClick={() => setIsOpen(false)}>
                  Mi Carrito
                </Link>
                <Separator />
                {!user && !loading && (
                  <>
                    <Link to="/login" className="text-lg font-semibold" onClick={() => setIsOpen(false)}>
                      Ingresar
                    </Link>
                    <Link to="/register" className="text-lg font-semibold" onClick={() => setIsOpen(false)}>
                      Registrarse
                    </Link>
                  </>
                )}
                {user && (
                  <Button variant="ghost" className="justify-start px-0 text-lg font-semibold text-red-600" onClick={() => {
                    handleLogout();
                    setIsOpen(false);
                  }}>
                    Cerrar Sesión
                  </Button>
                )}
              </div>
            </SheetContent>
          </Sheet>

        </div>
      </div>
    </nav>
  );
}

